import { episodes } from "@/mocks/episodes";
import type { Episode } from "@/types/episode";

export type SortOrder = "update" | "number-asc" | "number-desc";

export const allEpisodes: Episode[] = episodes as Episode[];

export const pageSize = 12;

export const getEpisodeById = (episodeId?: string): Episode | undefined =>
  allEpisodes.find((episode) => episode.id === episodeId);

export const sortEpisodes = (list: Episode[], order: SortOrder): Episode[] => {
  const sorted = [...list];
  if (order === "number-asc") sorted.sort((a, b) => a.number - b.number);
  if (order === "number-desc") sorted.sort((a, b) => b.number - a.number);
  if (order === "update") sorted.sort((a, b) => a.number - b.number);
  return sorted;
};

export const searchEpisodes = (keyword: string): Episode[] => {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return allEpisodes.filter((episode) => {
    const haystack = [
      episode.title,
      episode.tagline,
      episode.synopsis,
      episode.tags.join(" "),
      episode.story.join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
};

export const getAdjacentEpisodes = (episodeId: string) => {
  const index = allEpisodes.findIndex((episode) => episode.id === episodeId);
  if (index < 0) return { prev: undefined, next: undefined };
  return {
    prev: index > 0 ? allEpisodes[index - 1] : undefined,
    next: index < allEpisodes.length - 1 ? allEpisodes[index + 1] : undefined,
  };
};

export const getRecommendations = (episode: Episode, limit = 4): Episode[] => {
  const scored = allEpisodes
    .filter((item) => item.id !== episode.id)
    .map((item) => {
      const tagScore = item.tags.filter((tag) => episode.tags.includes(tag)).length;
      const distanceScore = Math.max(0, 3 - Math.abs(item.number - episode.number));
      return { item, score: tagScore * 2 + distanceScore };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
};

export const latestEpisodes = [...allEpisodes]
  .sort((a, b) => b.number - a.number)
  .slice(0, 4);

export const favoriteEpisodes = (ids: string[]): Episode[] =>
  allEpisodes.filter((episode) => ids.includes(episode.id));